import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  LinearProgress,
  Typography,
} from '@mui/material'
import Grid from '@mui/material/Grid2'
import { Refresh } from '@mui/icons-material'
import type { JSX, ReactNode, SubmitEvent } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  calibrationCard,
  calibrationCardActions,
  warningCardHeader,
} from '@/utils/theme'
import CompositionCheckTable from '@/components/CompositionCheckTable'
import type { Composition } from '@/pages/calibration/composition'
import DeviceType from '@/pages/Device/DeviceType'

const isCompositionValid = (composition: Composition | null): boolean => {
  if (!composition) return false
  return composition.mismatches.length === 0
}

const isProductionEnv = (): boolean =>
  window.location.href.includes('sam.dualinventive.com')

const RefreshButton = ({
  onClick,
  title,
}: {
  onClick: () => void
  title: string
}): JSX.Element => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    title={title}
    aria-label={title}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 32,
      height: 32,
      borderRadius: '50%',
      border: 'none',
      cursor: 'pointer',
      flexShrink: 0,
      backgroundColor: 'takeoutofservice.main',
      color: '#fff',
      '&:hover': { backgroundColor: 'takeoutofservice.dark' },
      '& svg': { fontSize: '1.1rem' },
    }}
  >
    <Refresh fontSize="small" />
  </Box>
)

interface Props {
  serial: string
  label?: string
  type: string
  isSaving: boolean
  nextDisabled?: boolean
  composition: Composition | null
  onBack: () => void
  onFormSubmit: (
    e: React.BaseSyntheticEvent,
    compositionCheckPasses: boolean,
  ) => void | Promise<void>
  onRefreshComposition: () => void
  children: ReactNode
}

const PreFlightStepBase = ({
  serial,
  label,
  type,
  isSaving,
  nextDisabled = false,
  composition,
  onBack,
  onFormSubmit,
  onRefreshComposition,
  children,
}: Props): JSX.Element => {
  const { t } = useTranslation('common')

  const [ignoreValueCheck, setIgnoreValueCheck] = useState<boolean>(false)
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)

  const headerTitle = label ? `${serial} - ${label}` : serial
  const compositionCheckPasses =
    ignoreValueCheck || isCompositionValid(composition)
  const showCompositionError = hasAttemptedSubmit && !compositionCheckPasses

  const handleSubmit = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault()
    setHasAttemptedSubmit(true)
    void onFormSubmit(e, compositionCheckPasses)
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      data-testid="preflight-step-form"
    >
      <Box sx={calibrationCard}>
        <Box sx={warningCardHeader}>
          <Typography
            component="h4"
            sx={{
              color: '#fff',
              fontWeight: 300,
              fontSize: '18px',
              lineHeight: '1.375rem',
              margin: '0 0 5px 0',
            }}
          >
            {headerTitle}
          </Typography>
          <Typography
            component="h4"
            sx={{
              color: '#fff',
              fontWeight: 300,
              fontSize: '18px',
              lineHeight: '1.375rem',
              margin: 0,
            }}
          >
            {DeviceType[type as keyof typeof DeviceType] ?? type}
          </Typography>
        </Box>

        <Box sx={{ padding: '0.9375rem 20px' }}>
          <Grid container>
            <Grid
              size={{ xs: 12, sm: 6, md: 5, lg: 4, xl: 3 }}
              sx={{ padding: '0 15px !important' }}
            >
              {children}
            </Grid>
            <Grid
              size={{ xs: 12, lg: 8, xl: 9 }}
              sx={{ padding: '0 15px !important' }}
            >
              <FormControl
                fullWidth
                sx={{ margin: '0 0 17px 0', paddingTop: '27px' }}
              >
                <InputLabel
                  shrink
                  error={showCompositionError}
                  sx={{ mb: '3px', paddingTop: '2px' }}
                >
                  {t('device.calibration.preFlightStep.valueChecks')}
                </InputLabel>

                {!isProductionEnv() && (
                  <FormControlLabel
                    label={t(
                      'device.calibration.preFlightStep.ignoreValueCheck',
                    )}
                    control={
                      <Checkbox
                        checked={ignoreValueCheck}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setIgnoreValueCheck(e.target.checked)
                        }}
                        name="ignoreValueCheck"
                      />
                    }
                    name="ignoreValueCheckLabel"
                  />
                )}

                <CompositionCheckTable
                  composition={composition}
                  updatedAtPrefix={
                    <RefreshButton
                      onClick={onRefreshComposition}
                      title={t('general.refresh')}
                    />
                  }
                  caption={
                    showCompositionError ? (
                      <FormHelperText error>
                        {t('device.calibration.preFlightStep.valueChecksError')}
                      </FormHelperText>
                    ) : undefined
                  }
                />
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        {isSaving && (
          <LinearProgress
            variant="indeterminate"
            color="primary"
            sx={{
              borderBottomLeftRadius: 1,
              borderBottomRightRadius: 1,
              mb: 0,
            }}
          />
        )}
      </Box>

      <Box sx={calibrationCardActions}>
        <Button
          variant="contained"
          onClick={onBack}
          disabled={isSaving}
          data-testid="preflight-back-button"
          sx={{ marginTop: 2, marginRight: 2 }}
        >
          {t('general.back')}
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="success"
          disabled={isSaving || nextDisabled}
          data-testid="preflight-next-button"
          sx={{ marginTop: 2, marginRight: 2 }}
        >
          {t('general.next')}
        </Button>
      </Box>
    </Box>
  )
}

export default PreFlightStepBase
